import { useDrawer } from '@dashhudson/dashing-ui/drawer';
import { computed, nextTick } from 'vue';
import { useRouter } from 'vue-router';

import {
  SCHEDULER_ADD_NEW_DROPDOWN_OPTIONS,
  SCHEDULER_ADD_NEW_OPTIONS_INSTAGRAM,
  SCHEDULER_DRAWERS,
} from '@/app/scheduler/constants';
import { constants } from '@/config';
import { useTrackingStore } from '@/stores/tracking';

export const useNewAnalyticsConnection = (platform) => {
  const trackingStore = useTrackingStore();
  const router = useRouter();

  const newConnectionHeader = 'Your Published Posts Will Appear Here';

  const newConnectionButtonText = computed(() => {
    switch (platform) {
      case constants.THREADS:
      case constants.YOUTUBE:
        return undefined;
      case constants.LIBRARY:
        return 'Upload Media';
      default:
        return 'Draft Post';
    }
  });

  const newConnectionMessage = computed(() => {
    switch (platform) {
      case constants.THREADS:
      case constants.YOUTUBE:
        return undefined;
      case constants.LIBRARY:
        return 'While you wait, upload media and save time scheduling your content';
      default:
        return "If you haven't published any posts yet, try drafting a post in Scheduler";
    }
  });

  async function goToUploadMedia() {
    await router.push({ name: 'library.media.upload' });
  }

  async function goToPlatformIndex() {
    await router.push({
      name: `scheduler.${
        platform === constants.INSTAGRAM_STORY ? 'instagram' : platform.toLowerCase()
      }.timeline`,
    });

    const schedulePlatform =
      platform === constants.INSTAGRAM_STORY ? 'instagram.story' : platform.toLowerCase();
    const option = [
      ...SCHEDULER_ADD_NEW_DROPDOWN_OPTIONS,
      ...SCHEDULER_ADD_NEW_OPTIONS_INSTAGRAM,
    ].find((addNewOption) => addNewOption.action === schedulePlatform);

    const { launchDrawer } = useDrawer();
    await nextTick();
    await launchDrawer({
      name: SCHEDULER_DRAWERS.EDIT_POST_DRAWER,
      props: {
        initialPostType: option.postType,
      },
    });
  }

  async function goToSchedulerPage() {
    if (platform === constants.THREADS || platform === constants.YOUTUBE) {
      return;
    }
    trackingStore.track('Empty State - Call To Action', {
      buttonWords: newConnectionButtonText.value,
    });
    switch (platform) {
      case constants.LIBRARY:
        await goToUploadMedia();
        break;
      default:
        await goToPlatformIndex();
        break;
    }
  }

  const defaultEmptyState = computed(() => {
    if (platform === constants.THREADS || platform === constants.YOUTUBE) {
      return {
        center: true,
        icon: 'image',
        title: newConnectionHeader,
        message: null,
      };
    }

    return {
      center: true,
      icon: 'image',
      title: newConnectionHeader,
      message: newConnectionMessage.value,
      ctaButton: newConnectionButtonText.value
        ? {
            label: newConnectionButtonText.value,
            severity: 'primary',
          }
        : undefined,
      onCtaClick: goToSchedulerPage,
    };
  });

  const noMatchEmptyState = {
    center: true,
    variant: 'sectionEmpty',
    icon: 'filter',
    title: "Couldn't Find a Match",
    message: 'Review or adjust your filters to uncover more results',
    ctaButton: {
      label: 'Reset Filters',
      severity: 'tertiary',
    },
  };

  return {
    defaultEmptyState,
    noMatchEmptyState,
    goToSchedulerPage,
  };
};
